export type LogTimestampFormatters = {
  year: Intl.DateTimeFormat;
  month: Intl.DateTimeFormat;
  day: Intl.DateTimeFormat;
  time: Intl.DateTimeFormat;
};

/**
 * formatLogTimestamp renders 'date' as 'YYYY-MM-DD HH:MM:SS' using 'formatters',
 * matching DateFormat.YYYY_MM_DD_HH_MM_SS_DASHES.
 */
export function formatLogTimestamp(
  date: Date,
  formatters: LogTimestampFormatters
): string {
  const year = formatters.year.format(date);
  const month = formatters.month.format(date);
  const day = formatters.day.format(date);
  const time = formatters.time.format(date);
  return `${year}-${month}-${day} ${time}`;
}

/**
 * buildLogTimestampFormatters returns the formatters that render a timestamp in
 * 'timeZone'. Build once per zone and reuse across lines.
 */
export function buildLogTimestampFormatters(
  timeZone: string
): LogTimestampFormatters {
  // A falsy zone, which some platforms report, would make Intl throw; fall
  // back to the ambient default instead.
  const tz = timeZone || undefined;
  // NOTE: The shared date.ts format() rebuilds four Intl formatters per call,
  // too costly for the per-line path, so build the set once and reuse it.
  return {
    year: new Intl.DateTimeFormat('en-US', {year: 'numeric', timeZone: tz}),
    month: new Intl.DateTimeFormat('en-US', {month: '2-digit', timeZone: tz}),
    day: new Intl.DateTimeFormat('en-US', {day: '2-digit', timeZone: tz}),
    time: new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
      timeZone: tz,
    }),
  };
}

/**
 * hostTimeZone returns the IANA name of the runtime's current time zone.
 */
export function hostTimeZone(): string {
  return new Intl.DateTimeFormat().resolvedOptions().timeZone;
}
